import { useCallback, useEffect, useMemo } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useDebounceValue } from 'usehooks-ts';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  ENDPOINT_MEMORY_OVERLAYS_QUERY_KEY,
  MEMORY_EXTRACTION_POLL_INTERVAL_MS,
  endpointMemoriesRefetchInterval,
  shouldPollEndpointMemories,
  isSuppressedMemoryRecordId,
  mergeMemoriesWithOverlays,
  readPersistedEndpointMemoryOverlays,
  resolveMemoryOverlays,
  writePersistedEndpointMemoryOverlays,
  type EndpointMemoryOverlay,
} from '../endpointMemoryOverlays';

export const ENDPOINT_MEMORIES_QUERY_KEY = 'endpoint-memories';

const DEFAULT_PAGE_SIZE = 250;

export const getUnfilteredEndpointMemoriesQueryKey = ({
  applicationId,
  endpointId,
}: {
  applicationId: string;
  endpointId: string;
}) =>
  [
    ENDPOINT_MEMORIES_QUERY_KEY,
    applicationId,
    endpointId,
    '',
    0,
    DEFAULT_PAGE_SIZE,
  ] as const;

type UseEndpointMemoriesParams = {
  applicationId: string | undefined;
  endpointId: string | undefined;
  searchTerm?: string;
  skip?: number;
  limit?: number;
  enabled?: boolean;
};

type UseEndpointMemoriesReturn = {
  memories: MemoryRecordResponse[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  invalidate: () => Promise<void>;
};

export function useEndpointMemories({
  applicationId,
  endpointId,
  searchTerm = '',
  skip = 0,
  limit = DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseEndpointMemoriesParams): UseEndpointMemoriesReturn {
  const queryClient = useQueryClient();
  const t = useTranslations('memories');
  const categoryMismatchReason = t('rejectionReason.categoryMismatch');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);

  const overlaysQueryKey = useMemo(
    () =>
      [ENDPOINT_MEMORY_OVERLAYS_QUERY_KEY, applicationId, endpointId] as const,
    [applicationId, endpointId],
  );

  const { data: overlays = [] } = useQuery({
    queryKey: overlaysQueryKey,
    queryFn: async (): Promise<EndpointMemoryOverlay[]> =>
      queryClient.getQueryData<EndpointMemoryOverlay[]>(overlaysQueryKey) ??
      (applicationId && endpointId
        ? readPersistedEndpointMemoryOverlays({ applicationId, endpointId })
        : []),
    initialData: () =>
      applicationId && endpointId
        ? readPersistedEndpointMemoryOverlays({ applicationId, endpointId })
        : [],
    enabled: enabled && Boolean(applicationId) && Boolean(endpointId),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (!applicationId || !endpointId) {
      return;
    }

    writePersistedEndpointMemoryOverlays({
      applicationId,
      endpointId,
      overlays,
    });
  }, [applicationId, endpointId, overlays]);

  const hasPendingOverlay = overlays.some(
    (overlay) => overlay.status === 'pending',
  );

  const unfilteredQueryKey = useMemo(
    () =>
      applicationId && endpointId
        ? getUnfilteredEndpointMemoriesQueryKey({ applicationId, endpointId })
        : ([
            ENDPOINT_MEMORIES_QUERY_KEY,
            applicationId,
            endpointId,
            '',
            0,
            DEFAULT_PAGE_SIZE,
          ] as const),
    [applicationId, endpointId],
  );

  const queryResult = useQuery({
    queryKey: [
      ENDPOINT_MEMORIES_QUERY_KEY,
      applicationId,
      endpointId,
      debouncedSearchTerm,
      skip,
      limit,
    ],
    queryFn: async () => {
      if (!applicationId || !endpointId) {
        return { records: [], totalCount: 0 };
      }

      return terraProxyApiClient.getMemoriesByEndpoint(
        applicationId,
        endpointId,
        {
          skip,
          limit,
          search: debouncedSearchTerm || undefined,
        },
      );
    },
    enabled: enabled && Boolean(applicationId) && Boolean(endpointId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      endpointMemoriesRefetchInterval({
        overlays,
        records: query.state.data?.records ?? [],
      }),
  });

  const shouldPoll = shouldPollEndpointMemories({
    overlays,
    records: queryResult.data?.records ?? [],
  });

  const resolutionQueryResult = useQuery({
    queryKey: unfilteredQueryKey,
    queryFn: async () => {
      if (!applicationId || !endpointId) {
        return { records: [], totalCount: 0 };
      }

      return terraProxyApiClient.getMemoriesByEndpoint(
        applicationId,
        endpointId,
        {
          skip: 0,
          limit: DEFAULT_PAGE_SIZE,
        },
      );
    },
    enabled:
      enabled && Boolean(applicationId) && Boolean(endpointId) && shouldPoll,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    refetchInterval: (query) =>
      endpointMemoriesRefetchInterval({
        overlays,
        records: query.state.data?.records ?? [],
      }),
  });

  const bedrockRecords = useMemo(
    () => queryResult.data?.records ?? [],
    [queryResult.data?.records],
  );

  useEffect(() => {
    if (!applicationId || !endpointId || !categoryMismatchReason) {
      return;
    }

    const applyResolution = () => {
      const currentOverlays =
        queryClient.getQueryData<EndpointMemoryOverlay[]>(overlaysQueryKey) ??
        [];

      if (currentOverlays.length === 0) {
        return;
      }

      const resolutionRecords = queryClient.getQueryData<{
        records: MemoryRecordResponse[];
      }>(unfilteredQueryKey)?.records;

      if (!resolutionRecords) {
        return;
      }

      const nextOverlays = resolveMemoryOverlays({
        overlays: currentOverlays,
        bedrockRecords: resolutionRecords,
        categoryMismatchReason,
      });

      const unchanged =
        nextOverlays.length === currentOverlays.length &&
        nextOverlays.every((overlay, index) => {
          const current = currentOverlays[index];

          return (
            overlay.recordId === current?.recordId &&
            overlay.status === current?.status &&
            overlay.rejectionReason === current?.rejectionReason &&
            overlay.beforeSnapshot.length === current.beforeSnapshot.length &&
            overlay.beforeSnapshot.every(
              (snapshot, snapshotIndex) =>
                snapshot.recordId ===
                  current.beforeSnapshot[snapshotIndex]?.recordId &&
                snapshot.content ===
                  current.beforeSnapshot[snapshotIndex]?.content &&
                snapshot.createdAt ===
                  current.beforeSnapshot[snapshotIndex]?.createdAt,
            )
          );
        });

      if (!unchanged) {
        queryClient.setQueryData(overlaysQueryKey, nextOverlays);
        writePersistedEndpointMemoryOverlays({
          applicationId,
          endpointId,
          overlays: nextOverlays,
        });
      }
    };

    applyResolution();

    if (!hasPendingOverlay) {
      return;
    }

    const intervalId = window.setInterval(
      applyResolution,
      MEMORY_EXTRACTION_POLL_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    applicationId,
    categoryMismatchReason,
    endpointId,
    hasPendingOverlay,
    overlaysQueryKey,
    queryClient,
    resolutionQueryResult.dataUpdatedAt,
    unfilteredQueryKey,
  ]);

  const memories = mergeMemoriesWithOverlays({
    bedrockRecords,
    overlays,
  });

  const bedrockIds = useMemo(
    () => new Set(bedrockRecords.map((record) => record.recordId)),
    [bedrockRecords],
  );

  const totalCount = useMemo(() => {
    const apiTotalCount = queryResult.data?.totalCount ?? 0;
    const additiveOverlayCount = overlays.filter(
      (overlay) =>
        !isSuppressedMemoryRecordId(overlay.recordId) &&
        !bedrockIds.has(overlay.recordId) &&
        !(overlay.status === 'pending' && overlay.replacesRecordId),
    ).length;
    const suppressedCount = overlays.filter(
      (overlay) =>
        isSuppressedMemoryRecordId(overlay.recordId) &&
        Boolean(overlay.replacesRecordId),
    ).length;

    return Math.max(apiTotalCount + additiveOverlayCount - suppressedCount, 0);
  }, [bedrockIds, overlays, queryResult.data?.totalCount]);

  const refetch = useCallback(async () => {
    await queryResult.refetch();
  }, [queryResult]);

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [ENDPOINT_MEMORIES_QUERY_KEY],
    });
  }, [queryClient]);

  return {
    memories,
    totalCount,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch,
    invalidate,
  };
}
